import type { Slug } from '@bridebook/toolbox/src/types';
import { TFilterProps, TFiltersPerSupplierCategory } from '@bridebook/web/lib/supplier/types';

const defaultFiltersValuesPerSupplierCategory: TFilterProps = {
  beauty: {
    renderSections: ['productsServicesSection'],
    productsServicesSection: {
      airbrush: false,
      beautySalons: false,
      cosmeticSurgery: false,
      delivery: false,
      groupBookings: false,
      hairdressing: false,
      hairExtensions: false,
      laserHairRemoval: false,
      makeUpArtists: false,
      massage: false,
      mobileServices: false,
      nailArtists: false,
      nutritionist: false,
      onlineShop: false,
      onTheDayServices: false,
      personalTrainer: false,
      physicalShopStudio: false,
      preWeddingConsultation: false,
      preWeddingTrial: false,
      stayThroughCeremony: false,
      stayThroughDancing: false,
      stayThroughReception: false,
      tanning: false,
      tattooCoverUpMakeUp: false,
      teethWhitening: false,
      threading: false,
      waxing: false,
    },
  },
  cakes: {
    renderSections: [
      'dietaryOptionsSection',
      'generalServicesSection',
      'icingOptionsSection',
      'flavourOptionsSection',
    ],
    dietaryOptionsSection: {
      dairyFree: false,
      glutenFree: false,
      halal: false,
      kosher: false,
      nutFree: false,
      vegan: false,
      vegetarian: false,
    },
    generalServicesSection: {
      bespokeDesign: false,
      cakeAccessories: false,
      cupcakes: false,
      delivery: false,
      lastMinuteOrders: false,
      physicalShopStudio: false,
      preWeddingConsultation: false,
      setUp: false,
      sheetCakes: false,
      tastings: false,
      tieredCakes: false,
      treatsAndFavours: false,
      donuts: false,
      brownies: false,
      cheeseBoardPlatter: false,
      macarons: false,
    },
    icingOptionsSection: {
      buttercream: false,
      fondant: false,
      naked: false,
      semiNaked: false,
    },
    flavourOptionsSection: {
      berries: false,
      carrot: false,
      chocolate: false,
      coffee: false,
      cookiesCream: false,
      fruitCake: false,
      lemon: false,
      saltedCaramel: false,
      otherFlavour: false,
      redVelvet: false,
      raspberryWhiteChocolate: false,
      vanilla: false,
    },
  },
  catering: {
    renderSections: ['dietaryOptionsSection', 'foodTypeSection', 'generalServicesSection'],
    dietaryOptionsSection: {
      dairyFree: false,
      glutenFree: false,
      halal: false,
      kosher: false,
      nutFree: false,
      vegan: false,
      vegetarian: false,
    },
    foodTypeSection: {
      asianCuisine: false,
      barCocktailService: false,
      caribbeanCuisine: false,
      chineseJapaneseCuisine: false,
      hogRoast: false,
      latinSouthAmericanCuisine: false,
      middleEasternCuisine: false,
      westernEuropeanCuisine: false,
    },
    generalServicesSection: {
      alcoholProvision: false,
      bartending: false,
      buffetStyle: false,
      canapes: false,
      cleanUp: false,
      foodVan: false,
      lastMinuteOrders: false,
      linenProvided: false,
      marqueeCatering: false,
      midnightTreats: false,
      minimumGuestNumbers: false,
      mobileBarServices: false,
      offSitePreparation: false,
      physicalKitchenPremises: false,
      preWeddingConsultation: false,
      seatedMeal: false,
      setUp: false,
      sharingStyle: false,
      silverService: false,
      staffProvided: false,
      tablesAndChairsProvided: false,
      tablewareProvided: false,
      tastings: false,
      weddingCake: false,
    },
  },
  decoration: {
    renderSections: ['productsServicesSection'],
    productsServicesSection: {
      arcadeGameHire: false,
      balloons: false,
      barHire: false,
      bunting: false,
      candelabras: false,
      candles: false,
      chairCovers: false,
      cleanUp: false,
      completeStylingService: false,
      confetti: false,
      dancefloorHire: false,
      decorativeHireProps: false,
      delivery: false,
      diningFurnitureHire: false,
      generator: false,
      giantLetters: false,
      greeneryHire: false,
      heating: false,
      lastMinuteOrders: false,
      lighting: false,
      loungeFurnitureHire: false,
      luxuryToilets: false,
      microphones: false,
      portaloos: false,
      preWeddingConsultation: false,
      projector: false,
      setUp: false,
      showroom: false,
      soundPaHire: false,
    },
  },
  dress: {
    renderSections: ['productsServicesSection'],
    productsServicesSection: {
      accessories: false,
      alterations: false,
      appointmentOnly: false,
      bags: false,
      bespokeDressmaker: false,
      bridalBoutique: false,
      bridesmaidDresses: false,
      delivery: false,
      dressDesigner: false,
      dressHire: false,
      dryCleaning: false,
      flowerGirlDresses: false,
      hatsFascinators: false,
      highStreetRetailer: false,
      lastMinuteOrders: false,
      lingerie: false,
      motherOfTheBrideDresses: false,
      onlineShop: false,
      physicalShopStudio: false,
      preWeddingConsultation: false,
      shoes: false,
      tiaras: false,
      veils: false,
      weddingDresses: false,
    },
  },
  entertainment: {
    renderSections: ['productsServicesSection'],
    productsServicesSection: {
      bouncyCastleHire: false,
      candyCarts: false,
      caricaturists: false,
      casinos: false,
      childrenEntertainment: false,
      chocolateFountains: false,
      danceLessons: false,
      dancers: false,
      doves: false,
      facePaint: false,
      fireworks: false,
      hotTubHire: false,
      iceSculptures: false,
      lawnGames: false,
      livePainting: false,
      magicians: false,
      photoAndVideoBooth: false,
      preWeddingConsultation: false,
    },
  },
  florist: {
    renderSections: ['productsServicesSection', 'floristStylesSection', 'floristEcoOptionsSection'],
    productsServicesSection: {
      aisleDecorations: false,
      bouquetPreservation: false,
      bouquets: false,
      buttonholes: false,
      centerpieces: false,
      chuppah: false,
      cleanUp: false,
      corsages: false,
      delivery: false,
      driedFlowers: false,
      flowerConfetti: false,
      flowerCrowns: false,
      flowerGirlBaskets: false,
      freshFlowers: false,
      greeneryHire: false,
      lastMinuteOrders: false,
      minimumOrder: false,
      onlineShop: false,
      physicalShopStudio: false,
      preWeddingConsultation: false,
      setUp: false,
      silkFlowers: false,
      vases: false,
      weddingArches: false,
      tablescaping: false,
    },
    floristStylesSection: {
      bohemian: false,
      classic: false,
      modern: false,
      natural: false,
      romantic: false,
      rustic: false,
      vintage: false,
      whimsical: false,
    },
    floristEcoOptionsSection: {
      biodegradable: false,
      foamFree: false,
      localSourcing: false,
      seasonalSourcing: false,
      toxicChecmicalFree: false,
    },
  },
  jewellery: {
    renderSections: ['productsServicesSection'],
    productsServicesSection: {
      accessories: false,
      bespokeDesigns: false,
      broaches: false,
      cufflinks: false,
      delivery: false,
      engagementRings: false,
      engraving: false,
      fittings: false,
      gemstones: false,
      gifts: false,
      gold: false,
      highEndRetailer: false,
      highStreetRetailer: false,
      jewellery: false,
      lastMinuteOrders: false,
      onlineShop: false,
      physicalShopStudio: false,
      platinum: false,
      preWeddingConsultation: false,
      silver: false,
      watches: false,
      weddingRings: false,
    },
  },
  marquee: {
    renderSections: ['productsServicesSection'],
    productsServicesSection: {
      carpet: false,
      chairs: false,
      clearCeiling: false,
      dancefloor: false,
      divideAndRevealCurtain: false,
      flooring: false,
      frameClearspanMarquees: false,
      generator: false,
      heating: false,
      lastMinuteOrders: false,
      lighting: false,
      lining: false,
      luxuryToilets: false,
      otherFurniture: false,
      peakedHatMarquees: false,
      portaloos: false,
      preWeddingConsultation: false,
      starlightLining: false,
      stretchedMarquee: false,
      tables: false,
      tipisWigwams: false,
      traditionalCanvasPoleMarquees: false,
    },
  },
  menswear: {
    renderSections: ['productsServicesSection'],
    productsServicesSection: {
      accessories: false,
      bespokeTailoring: false,
      blackTie: false,
      delivery: false,
      highStreetRetailer: false,
      kilts: false,
      lastMinuteOrders: false,
      loungeSuits: false,
      morningSuits: false,
      onlineShop: false,
      pageboyAttire: false,
      physicalShopStudio: false,
      preWeddingConsultation: false,
      shoes: false,
      suitHire: false,
      ties: false,
      waistcoats: false,
    },
  },
  music: {
    renderSections: [
      'additionalServicesSection',
      'businessTypeSection',
      'instrumentsSection',
      'musicStyleSection',
    ],
    additionalServicesSection: {
      dancefloorHire: false,
      discoBall: false,
      discoLighting: false,
      karaoke: false,
      learnNewSongs: false,
      mcAvailable: false,
      noiseLimiterExperience: false,
      paSystem: false,
      preWeddingConsultation: false,
      smokeMachine: false,
      soundEquipment: false,
      takeRequests: false,
    },
    businessTypeSection: {
      agency: false,
      choir: false,
      disco: false,
      dj: false,
      ensembleGroup: false,
      functionBand: false,
      jazzBand: false,
      soloMusician: false,
      soloSinger: false,
    },
    instrumentsSection: {
      bagpipes: false,
      brass: false,
      churchBells: false,
      completeBand: false,
      drums: false,
      femaleVocals: false,
      guitar: false,
      harp: false,
      maleVocals: false,
      organ: false,
      otherInstruments: false,
      piano: false,
      strings: false,
      violin: false,
      winds: false,
    },
    musicStyleSection: {
      acoustic: false,
      ceilidhIrishTraditional: false,
      ceremonyMusic: false,
      classical: false,
      drinksCocktailsMusic: false,
      funk70SDisco: false,
      jazz: false,
      latinSalsa: false,
      otherStyleOfMusic: false,
      popRock: false,
      rapBeatbox: false,
      ratPackSwing: false,
      receptionMusic: false,
      soulMotownRAndB: false,
      swingJive: false,
      tribute: false,
    },
  },
  photo: {
    renderSections: ['photographyStyleSection', 'productsServicesSection'],
    photographyStyleSection: {
      artistic: false,
      dramatic: false,
      firstLookPhotographer: false,
      modern: false,
      reportageDocumentary: false,
      traditionalClassic: false,
    },
    productsServicesSection: {
      backUpEquipment: false,
      bridalPrepShots: false,
      digitalEditingAirbrushing: false,
      flexibleHoursOnTheDay: false,
      imagesOnDvd: false,
      imagesOnUsb: false,
      largeGroupShots: false,
      lastMinuteOrders: false,
      onlineProofing: false,
      onlineShop: false,
      personalOnlineGallery: false,
      photoBoothAvailable: false,
      photoPrinting: false,
      physicalShopStudio: false,
      preWeddingConsultation: false,
      preWeddingVenueVisit: false,
      secondShooterAvailable: false,
      shootWithFilm: false,
      stayThroughDancing: false,
      stayThroughSpeeches: false,
      thankYouCardsPhotoStationery: false,
      videographyAvailable: false,
      weddingAlbums: false,
    },
  },
  planners: {
    renderSections: ['productsServicesSection'],
    productsServicesSection: {
      celebrantOfficiant: false,
      fullServicePlanning: false,
      inboundDestinationWeddingSpecialist: false,
      lastMinuteOrders: false,
      marqueeWeddingSpecialist: false,
      onTheDayWeddingCoordination: false,
      outboundDestinationWeddingSpecialist: false,
      physicalShopStudio: false,
      preWeddingConsultation: false,
      speechWriter: false,
      toastmasterMasterOfCeremony: false,
      vocalCoach: false,
      weekOfWeddingPlanning: false,
    },
  },
  stationery: {
    renderSections: ['productsServicesSection'],
    productsServicesSection: {
      albumsGuestbooks: false,
      bespokeDesign: false,
      catalogueSupplier: false,
      diyInvitations: false,
      engravedStationery: false,
      freeSamples: false,
      handCalligraphy: false,
      handmade: false,
      laserCutStationery: false,
      lastMinuteOrders: false,
      letterpressStationery: false,
      onlineInvitations: false,
      onlineShop: false,
      physicalShopStudio: false,
      pocketfoldStationery: false,
      preWeddingConsultation: false,
      printing: false,
      programsPlaceCardsMenus: false,
      recycledPaperOptions: false,
      saveTheDatesThankYouNotes: false,
      signs: false,
      tablePlan: false,
      weddingInvitations: false,
    },
  },
  transport: {
    renderSections: ['productsServicesSection'],
    productsServicesSection: {
      americanClassic: false,
      astonMartin: false,
      availableForPhotoShoot: false,
      beauford: false,
      bentley: false,
      busAndCoachHire: false,
      camperVans: false,
      chauffeurService: false,
      daimler: false,
      doubleDeckerBus: false,
      ferrari: false,
      helicopter: false,
      horseAndCarriage: false,
      jaguar: false,
      lamborghini: false,
      lastMinuteOrders: false,
      londonCab: false,
      miniCooper: false,
      modernLuxuryCars: false,
      motorbikeSideCar: false,
      preWeddingConsultation: false,
      rollsRoyce: false,
      selfDriveAllowed: false,
      showroom: false,
      sportsCars: false,
      stretchLimos: false,
      taxis: false,
      vintageClassicCars: false,
    },
  },
  venue: {
    renderSections: ['ceremonyReceptionSection', 'foodDrinkSection', 'tags', 'venueSection'],
    ceremonyReceptionSection: {
      churchWithinWalkingDistance: false,
      confettiPermitted: false,
      fireworksPermitted: false,
      inHouseDancefloor: false,
      outdoorCeremonyLicence: false,
      soundSystemAvailable: false,
      weddingLicence: false,
    },
    foodDrinkSection: {
      alcoholLicense: false,
      asianCatering: false,
      bar: false,
      byobPermitted: false,
      corkageCharge: false,
      externalCateringPermitted: false,
      halalCatering: false,
      inHouseCatering: false,
      inHouseWineList: false,
      kosherCatering: false,
      vegan: false,
    },
    overviewSection: {
      highestReviewed: false,
      alcoholLicense: false,
      bestOfBritain: false,
      exclusiveUse: false,
      hasAccommodation: false,
      lateNightExtensionAvailable: false,
      weddingLicence: false,
    },
    venueFeatures: {
      ballroom: false,
      churchWithinWalkingDistance: false,
      confettiPermitted: false,
      disabledAccess: false,
      fireworksPermitted: false,
      inHouseDancefloor: false,
      inHouseWeddingCoordinator: false,
      landscapedGardens: false,
      marqueePermitted: false,
      onsiteParking: false,
      petFriendly: false,
      soundSystemAvailable: false,
      transportation: false,
      waterView: false,
      wifi: false,
    },
    venueSection: {
      ballroom: false,
      bridalChangingFacilities: false,
      cityView: false,
      disabledAccess: false,
      exclusiveUse: false,
      gradeListedBuilding: false,
      inHouseWeddingCoordinator: false,
      landscapedGardens: false,
      marqueePermitted: false,
      onsiteParking: false,
      openFlamePermitted: false,
      outdoorSpace: false,
      petFriendly: false,
      spaOnsite: false,
      waterView: false,
    },
    venueType: {
      barn: false,
      castle: false,
      cityHotelCityVenue: false,
      conferenceCentre: false,
      countryHouseManorHouse: false,
      cruiseBoatYacht: false,
      gardenOutdoor: false,
      golfCourse: false,
      hotel: false,
      museumAttraction: false,
      otherVenueType: false,
      placeOfWorship: false,
      pubRestaurant: false,
      resort: false,
      rooftop: false,
      sportingVenueStadium: false,
      statelyHome: false,
      townHallRegistryOffice: false,
      uniqueVenueType: false,
      warehouseFactory: false,
      winery: false,
    },
    venueStyle: {
      affordable: false,
      alternative: false,
      asian: false,
      beach: false,
      blankCanvas: false,
      casual: false,
      classic: false,
      formal: false,
      greatViews: false,
      historic: false,
      intimate: false,
      lovelyGrounds: false,
      luxury: false,
      modern: false,
      outdoor: false,
      romantic: false,
      rustic: false,
      stylish: false,
      unique: false,
      unusual: false,
      urban: false,
    },
    diversityAndInclusion: {
      inclusivityLgbtqPlusExpert: false,
      inclusivityNeurodiverseFriendly: false,
    },
  },
  video: {
    renderSections: ['productsServicesSection'],
    productsServicesSection: {
      audioRetouching: false,
      backUpEquipment: false,
      bridalPrepShots: false,
      completeFootageOnDvdUsb: false,
      digitalEditing: false,
      dolliesAndGliders: false,
      droneFootage: false,
      engagementShoots: false,
      flexibleHoursOnTheDay: false,
      lastMinuteOrders: false,
      longEditMoreThan1Hour: false,
      marryoke: false,
      musicalOverlay: false,
      photoBoothAvailable: false,
      physicalShopStudio: false,
      preWeddingConsultation: false,
      sameDayHighlightFilm: false,
      secondShooterAvailable: false,
      shortEditMoreThan15Minutes: false,
      stayThroughDancing: false,
      stayThroughSpeeches: false,
      steadicam: false,
      weddingHighlightsFilm: false,
    },
  },
  group: {
    renderSections: [],
  },
};

const getFiltersPerSupplierCategory = (slug: Slug): TFiltersPerSupplierCategory =>
  defaultFiltersValuesPerSupplierCategory[slug];

export default getFiltersPerSupplierCategory;
